import { Arrays, UUID } from "@opendaw/lib-std";
import { ArpeggioDeviceBox, AudioEffectCompositeBox, AudioEffectCompositeCellBox, AutotuneDeviceBox, CompressorDeviceBox, ConvolverDeviceBox, CrusherDeviceBox, DattorroReverbDeviceBox, DelayDeviceBox, FoldDeviceBox, FrequencySplitBox, GateDeviceBox, GrooveShuffleBox, MaximizerDeviceBox, ModularAudioInputBox, ModularAudioOutputBox, ModularBox, ModularDeviceBox, ModuleConnectionBox, NeuralAmpDeviceBox, WclapDeviceBox, PitchDeviceBox, RevampDeviceBox, ReverbDeviceBox, StereoCompositeBox, StereoToolDeviceBox, AudioSinkDeviceBox, TidalDeviceBox, SpielwerkDeviceBox, VelocityDeviceBox, VocoderDeviceBox, WaveshaperDeviceBox, WerkstattDeviceBox, ZeitgeistDeviceBox } from "@opendaw/studio-boxes";
import { IconSymbol } from "@opendaw/studio-enums";
import { DeviceManualUrls } from "@opendaw/studio-adapters";
import { EffectParameterDefaults } from "./EffectParameterDefaults";
export var EffectFactories;
(function (EffectFactories) {
    // the engine's distributor maps the fixed entries by index
    const STEREO_ENTRY_COUNT = 2;
    const FREQUENCY_SPLIT_ENTRY_COUNT = 4;
    EffectFactories.Arpeggio = {
        defaultName: "Arpeggio",
        defaultIcon: IconSymbol.Stack,
        briefDescription: "Arpeggiator",
        description: "Generates rhythmic note sequences from chords",
        manualPage: DeviceManualUrls.Arpeggio,
        separatorBefore: false,
        external: false,
        type: "midi",
        boxName: "ArpeggioDeviceBox",
        create: ({ boxGraph }, hostField, index) => ArpeggioDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Arpeggio");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Pitch = {
        defaultName: "Pitch",
        defaultIcon: IconSymbol.Note,
        briefDescription: "Pitch Transformer",
        description: "Shifts the pitch of incoming notes",
        manualPage: DeviceManualUrls.Pitch,
        separatorBefore: false,
        external: false,
        type: "midi",
        boxName: "PitchDeviceBox",
        create: ({ boxGraph }, hostField, index) => PitchDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Pitch");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Velocity = {
        defaultName: "Velocity",
        defaultIcon: IconSymbol.Velocity,
        briefDescription: "Velocity Transformer",
        description: "Manipulates the velocity of incoming notes",
        manualPage: DeviceManualUrls.Velocity,
        separatorBefore: false,
        external: false,
        type: "midi",
        boxName: "VelocityDeviceBox",
        create: ({ boxGraph }, hostField, index) => VelocityDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Velocity");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Zeitgeist = {
        defaultName: "Zeitgeist",
        defaultIcon: IconSymbol.Zeitgeist,
        briefDescription: "Shuffle",
        description: "Distorts space and time",
        manualPage: DeviceManualUrls.Zeitgeist,
        separatorBefore: false,
        external: false,
        type: "midi",
        boxName: "ZeitgeistDeviceBox",
        create: ({ boxGraph, rootBoxAdapter }, hostField, index) => {
            const useGlobal = false; // TODO First Zeitgeist should be true
            const shuffleBox = useGlobal
                ? rootBoxAdapter.groove.box
                : GrooveShuffleBox.create(boxGraph, UUID.generate(), (box) => {
                    box.label.setValue("Shuffle");
                    box.duration.setValue(480);
                });
            return ZeitgeistDeviceBox.create(boxGraph, UUID.generate(), (box) => {
                box.label.setValue("Zeitgeist");
                box.groove.refer(shuffleBox);
                box.index.setValue(index);
                box.host.refer(hostField);
            });
        }
    };
    EffectFactories.Spielwerk = {
        defaultName: "Spielwerk",
        defaultIcon: IconSymbol.Code,
        briefDescription: "Scriptable FX",
        description: "User-scripted MIDI effect processor",
        manualPage: DeviceManualUrls.Spielwerk,
        separatorBefore: false,
        external: false,
        type: "midi",
        boxName: "SpielwerkDeviceBox",
        create: ({ boxGraph }, hostField, index) => SpielwerkDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Spielwerk");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.StereoTool = {
        defaultName: "Stereo Tool",
        defaultIcon: IconSymbol.Stereo,
        briefDescription: "Stereo Imaging",
        description: "Computes a stereo transformation matrix with volume, panning, phase inversion and stereo width.",
        manualPage: DeviceManualUrls.StereoTool,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "StereoToolDeviceBox",
        create: ({ boxGraph }, hostField, index) => StereoToolDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Stereo Tool");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Delay = {
        defaultName: "Delay",
        defaultIcon: IconSymbol.Time,
        briefDescription: "Echo FX",
        description: "Echoes the input signal with time-based repeats",
        manualPage: DeviceManualUrls.Delay,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "DelayDeviceBox",
        create: ({ boxGraph }, hostField, index) => DelayDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Delay");
            box.index.setValue(index);
            box.host.refer(hostField);
            box.version.setValue(1);
        })
    };
    EffectFactories.Convolver = {
        defaultName: "Convolver",
        defaultIcon: IconSymbol.Convolver,
        briefDescription: "Convolution Reverb",
        description: "Convolution effect: convolves the signal with an impulse-response sample (spaces, springs, cabinets)",
        manualPage: DeviceManualUrls.Convolver,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "ConvolverDeviceBox",
        create: ({ boxGraph }, hostField, index) => ConvolverDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Convolver");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.DattorroReverb = {
        defaultName: "Dattorro Reverb",
        defaultIcon: IconSymbol.Dattorro,
        briefDescription: "Reverb",
        description: "Dense algorithmic reverb based on Dattorro's design, capable of infinite decay",
        manualPage: DeviceManualUrls.DattorroReverb,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "DattorroReverbDeviceBox",
        create: ({ boxGraph }, hostField, index) => DattorroReverbDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Dattorro Reverb");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Maximizer = {
        defaultName: "Maximizer",
        defaultIcon: IconSymbol.Volume,
        briefDescription: "Brickwall Limiter",
        description: "Brickwall limiter with automatic makeup gain",
        manualPage: DeviceManualUrls.Maximizer,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "MaximizerDeviceBox",
        create: ({ boxGraph }, hostField, index) => MaximizerDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Maximizer");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Compressor = {
        defaultName: "Compressor",
        defaultIcon: IconSymbol.Compressor,
        briefDescription: "Compressor",
        description: "Reduces the dynamic range by attenuating signals above a threshold",
        manualPage: DeviceManualUrls.Compressor,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "CompressorDeviceBox",
        create: ({ boxGraph }, hostField, index) => CompressorDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Compressor");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Gate = {
        defaultName: "Gate",
        defaultIcon: IconSymbol.Gate,
        briefDescription: "Noise Gate",
        description: "Attenuates signals below a threshold to reduce noise",
        manualPage: DeviceManualUrls.Gate,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "GateDeviceBox",
        create: ({ boxGraph }, hostField, index) => GateDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Gate");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Reverb = {
        defaultName: "Free Reverb",
        defaultIcon: IconSymbol.Cube,
        briefDescription: "Reverb",
        description: "Simulates space and depth with reflections",
        manualPage: DeviceManualUrls.Reverb,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "ReverbDeviceBox",
        create: ({ boxGraph }, hostField, index) => ReverbDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Reverb");
            box.preDelay.setInitValue(0.001);
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Autotune = {
        defaultName: "Autotune",
        defaultIcon: IconSymbol.Note,
        briefDescription: "Pitch Correction",
        description: "Shifts the signal towards the nearest note of a key and scale",
        manualPage: DeviceManualUrls.Autotune,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "AutotuneDeviceBox",
        create: ({ boxGraph }, hostField, index) => AutotuneDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Autotune");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Crusher = {
        defaultName: "Crusher",
        defaultIcon: IconSymbol.Bug,
        briefDescription: "Bit Crusher",
        description: "Degrates the audio signal",
        manualPage: DeviceManualUrls.Crusher,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "CrusherDeviceBox",
        create: ({ boxGraph }, hostField, index) => CrusherDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Crusher");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Fold = {
        defaultName: "Fold",
        defaultIcon: IconSymbol.Fold,
        briefDescription: "Wavefolder",
        description: "Folds the signal back into audio-range",
        manualPage: DeviceManualUrls.Fold,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "FoldDeviceBox",
        create: ({ boxGraph }, hostField, index) => FoldDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Fold");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Waveshaper = {
        defaultName: "Waveshaper",
        defaultIcon: IconSymbol.Curve,
        briefDescription: "Waveshaper",
        description: "Applies nonlinear waveshaping distortion",
        manualPage: DeviceManualUrls.Waveshaper,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "WaveshaperDeviceBox",
        create: ({ boxGraph }, hostField, index) => WaveshaperDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Waveshaper");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Tidal = {
        defaultName: "Tidal",
        defaultIcon: IconSymbol.Tidal,
        briefDescription: "Tremolo & Autopan",
        description: "Shape rhythm and space through volume and pan.",
        manualPage: DeviceManualUrls.Tidal,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "TidalDeviceBox",
        create: ({ boxGraph }, hostField, index) => TidalDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Tidal");
            box.index.setValue(index);
            box.depth.setValue(0.75);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Revamp = {
        defaultName: "Revamp",
        defaultIcon: IconSymbol.EQ,
        briefDescription: "Graphical EQ",
        description: "Shapes the frequency balance of the sound",
        manualPage: DeviceManualUrls.Revamp,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "RevampDeviceBox",
        create: ({ boxGraph }, hostField, index) => RevampDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            EffectParameterDefaults.defaultRevampDeviceBox(box);
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.NeuralAmp = {
        defaultName: "Tone3000",
        defaultIcon: IconSymbol.Tone3000,
        briefDescription: "Amp Modeler",
        description: "Access thousands of amps, pedals, and cabs captured with Neural Amp Modeler.",
        manualPage: DeviceManualUrls.NeuralAmp,
        separatorBefore: false,
        external: true,
        type: "audio",
        boxName: "NeuralAmpDeviceBox",
        create: ({ boxGraph }, hostField, index) => NeuralAmpDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Tone3000");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Wclap = {
        defaultName: "WebCLAP",
        defaultIcon: IconSymbol.WebClap,
        briefDescription: "WebCLAP Plugin",
        description: "Hosts a CLAP plugin compiled to WebAssembly (WebCLAP bundle).",
        manualPage: DeviceManualUrls.Wclap,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "WclapDeviceBox",
        create: ({ boxGraph }, hostField, index) => WclapDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("WebCLAP");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Vocoder = {
        defaultName: "Vocoder",
        defaultIcon: IconSymbol.Vocoder,
        briefDescription: "Vocoder",
        description: "Classic analysis/synthesis vocoder with independent carrier and modulator ranges.",
        manualPage: DeviceManualUrls.Vocoder,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "VocoderDeviceBox",
        create: ({ boxGraph }, hostField, index) => VocoderDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Vocoder");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Werkstatt = {
        defaultName: "Werkstatt",
        defaultIcon: IconSymbol.Code,
        briefDescription: "Scriptable FX",
        description: "User-scripted DSP processor",
        manualPage: DeviceManualUrls.Werkstatt,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "WerkstattDeviceBox",
        create: ({ boxGraph }, hostField, index) => WerkstattDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Werkstatt");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Sink = {
        defaultName: "Sink",
        defaultIcon: IconSymbol.AudioBus,
        briefDescription: "Route to Bus",
        description: "Routes the signal at this position into an audio bus. Pass sets the level it continues down the chain at.",
        manualPage: DeviceManualUrls.Sink,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "AudioSinkDeviceBox",
        create: ({ boxGraph }, hostField, index) => AudioSinkDeviceBox.create(boxGraph, UUID.generate(), (box) => {
            box.label.setValue("Sink");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    EffectFactories.Modular = {
        defaultName: "🔇 Create New Modular Audio Effect (inaudible yet)",
        defaultIcon: IconSymbol.Box,
        briefDescription: "Modular System",
        description: "",
        manualPage: DeviceManualUrls.Modular,
        separatorBefore: true,
        external: false,
        type: "audio",
        boxName: "ModularDeviceBox",
        create: ({ boxGraph, rootBox, userEditingManager }, hostField, index) => {
            const moduleSetupBox = ModularBox.create(boxGraph, UUID.generate(), (box) => {
                box.collection.refer(rootBox.modularSetups);
                box.label.setValue("Modular");
            });
            const modularInput = ModularAudioInputBox.create(boxGraph, UUID.generate(), (box) => {
                box.attributes.collection.refer(moduleSetupBox.modules);
                box.attributes.label.setValue("Modular Input");
                box.attributes.x.setValue(-256);
                box.attributes.y.setValue(32);
            });
            const modularOutput = ModularAudioOutputBox.create(boxGraph, UUID.generate(), (box) => {
                box.attributes.collection.refer(moduleSetupBox.modules);
                box.attributes.label.setValue("Modular Output");
                box.attributes.x.setValue(256);
                box.attributes.y.setValue(32);
            });
            ModuleConnectionBox.create(boxGraph, UUID.generate(), (box) => {
                box.collection.refer(moduleSetupBox.connections);
                box.source.refer(modularInput.output);
                box.target.refer(modularOutput.input);
            });
            userEditingManager.modularSystem.edit(moduleSetupBox.editing);
            return ModularDeviceBox.create(boxGraph, UUID.generate(), (box) => {
                box.label.setValue("Modular");
                box.modularSetup.refer(moduleSetupBox.device);
                box.index.setValue(index);
                box.host.refer(hostField);
            });
        }
    };
    // A parallel FX stack: the user drags effects into its entries, so it starts EMPTY — an empty composite
    // passes its input through untouched, so inserting one never kills the chain.
    EffectFactories.AudioEffectComposite = {
        defaultName: "FX Composite",
        defaultIcon: IconSymbol.Stack,
        briefDescription: "Parallel FX stack",
        description: "Runs several effect chains in parallel and mixes them back (dry/wet)",
        manualPage: DeviceManualUrls.AudioEffectComposite,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "AudioEffectCompositeBox",
        create: ({ boxGraph }, hostField, index) => AudioEffectCompositeBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue("FX Composite");
            box.index.setValue(index);
            box.host.refer(hostField);
        })
    };
    // The stereo SPLIT: unlike the plain stack its entries are FIXED — the engine feeds entry 0 the left
    // channel and entry 1 the right, so the two are created here and the UI offers no add / remove.
    EffectFactories.StereoComposite = {
        defaultName: "Stereo Split",
        defaultIcon: IconSymbol.StereoSplit,
        briefDescription: "Per-channel split",
        description: "Processes the left and right channels through their own effect chains",
        manualPage: DeviceManualUrls.StereoComposite,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "StereoCompositeBox",
        create: ({ boxGraph }, hostField, index) => {
            const composite = StereoCompositeBox.create(boxGraph, UUID.generate(), box => {
                box.label.setValue("Stereo Split");
                box.index.setValue(index);
                box.host.refer(hostField);
            });
            // Entry 0 = left, entry 1 = right: the distributor maps them BY INDEX, so both must exist and
            // keep their order. Their chains start empty, which sums back to the untouched input.
            Arrays.create(entryIndex => AudioEffectCompositeCellBox.create(boxGraph, UUID.generate(), box => {
                box.composite.refer(composite.entries);
                box.index.setValue(entryIndex);
            }), STEREO_ENTRY_COUNT);
            return composite;
        }
    };
    EffectFactories.FrequencySplit = {
        defaultName: "Frequency Split",
        defaultIcon: IconSymbol.Charts,
        briefDescription: "Multiband split",
        description: "Splits the signal into frequency bands and processes each on its own chain",
        manualPage: DeviceManualUrls.FrequencySplit,
        separatorBefore: false,
        external: false,
        type: "audio",
        boxName: "FrequencySplitBox",
        create: ({ boxGraph }, hostField, index) => {
            const composite = FrequencySplitBox.create(boxGraph, UUID.generate(), box => {
                box.label.setValue("Frequency Split");
                box.index.setValue(index);
                box.host.refer(hostField);
            });
            Arrays.create(entryIndex => AudioEffectCompositeCellBox.create(boxGraph, UUID.generate(), box => {
                box.composite.refer(composite.entries);
                box.index.setValue(entryIndex);
            }), FREQUENCY_SPLIT_ENTRY_COUNT);
            return composite;
        }
    };
    EffectFactories.MidiNamed = {
        Arpeggio: EffectFactories.Arpeggio,
        Pitch: EffectFactories.Pitch,
        Spielwerk: EffectFactories.Spielwerk,
        Velocity: EffectFactories.Velocity,
        Zeitgeist: EffectFactories.Zeitgeist
    };
    EffectFactories.AudioNamed = {
        AudioEffectComposite: EffectFactories.AudioEffectComposite, // FX Composite
        StereoComposite: // FX Composite
        EffectFactories.StereoComposite, // Stereo Split
        FrequencySplit: // Stereo Split
        EffectFactories.FrequencySplit, // Frequency Split
        Autotune: // Frequency Split
        EffectFactories.Autotune,
        Compressor: EffectFactories.Compressor,
        Convolver: EffectFactories.Convolver,
        Crusher: EffectFactories.Crusher,
        DattorroReverb: EffectFactories.DattorroReverb, // Dattorro Reverb
        Delay: // Dattorro Reverb
        EffectFactories.Delay,
        Fold: EffectFactories.Fold,
        Reverb: EffectFactories.Reverb, // Free Reverb
        Gate: // Free Reverb
        EffectFactories.Gate,
        Maximizer: EffectFactories.Maximizer,
        Revamp: EffectFactories.Revamp,
        StereoTool: EffectFactories.StereoTool, // Stereo Tool
        Tidal: // Stereo Tool
        EffectFactories.Tidal,
        NeuralAmp: EffectFactories.NeuralAmp, // Tone3000
        Vocoder: // Tone3000
        EffectFactories.Vocoder,
        Waveshaper: EffectFactories.Waveshaper,
        Wclap: EffectFactories.Wclap, // WebCLAP
        Werkstatt: // WebCLAP
        EffectFactories.Werkstatt,
        Sink: EffectFactories.Sink
    };
    EffectFactories.MidiList = Object.values(EffectFactories.MidiNamed);
    EffectFactories.AudioList = Object.values(EffectFactories.AudioNamed);
    EffectFactories.MergedNamed = { ...EffectFactories.MidiNamed, ...EffectFactories.AudioNamed };
    EffectFactories.keyOfBox = (box) => Object.keys(EffectFactories.MergedNamed)
        .find(key => EffectFactories.MergedNamed[key].boxName === box.name);
})(EffectFactories || (EffectFactories = {}));
